import { Component, State, Listen, h, Watch } from "@stencil/core";
import { RouterEventDetail, getMode, loadingController } from "@ionic/core";
import { Unsubscribe } from "redux";
import { store, configureStore } from "../../store/index";
import { getAuth } from "../../actions/auth";
import { initSwApi } from "../../global/app";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import {
  checkNotificationsPermission,
  resetInactivityReminder,
} from "../../actions/notifications";
import {
  getStorageKey,
  loadStoredState,
  saveState,
} from "../../global/storage";
import { initGDPR } from "../../global/gdpr-web";
import { listenToAppStateChange } from "../../actions/app";
import { renderRouter } from "./render-router";
import {
  setInitialAudioDucking,
  increaseLaunchCount,
} from "../../actions/local-settings";
import { routeChanged } from "../../actions/router";
import { initExperiments } from "../../actions/experiments";
import { showNewVersionAvailableToast } from "../../global/toast";
import {
  amplitudeInit,
  amplitudeSetUserProperties,
  amplitudeLogEvent,
  AmplitudeEvents,
  sendSworkitOpenedToAmplitude,
} from "../../global/amplitude";
import { initSwWorkout } from "../../actions/workout-player";
import { iosTransitionAnimation } from "./ios-transition";
import { checkIfMilestoneCompleted } from "../../actions/user";
import { showPreloadErrorModal } from "../../global/preload-error-modal";
import { SplashScreen } from "@capacitor/splash-screen";
import { Preferences } from "@capacitor/preferences";
import { initRemoteConfig } from "../../global/native";
import { setUserAssignments } from "../../actions/user-assignments";
import { isInIframe } from "../../global/firebase";
import { initWatchTranslations } from "../../global/apple-watch/apple-watch";
import {
  getAvailableNativeHealthApps,
  syncAllHealth,
} from "../../actions-stencil-store/health";
import { Capacitor } from "@capacitor/core";
import { SafeArea } from "capacitor-plugin-safe-area";
import { darkModeManager } from "../../global/dark-mode";

@Component({
  tag: "sw-app",
  styleUrl: "sw-app.scss",
})
export class SwApp {
  unsubscribe: Unsubscribe;
  getAuth: typeof getAuth;
  resetInactivityReminder: typeof resetInactivityReminder;
  setInitialAudioDucking: typeof setInitialAudioDucking;
  listenToAppStateChange: typeof listenToAppStateChange;
  routeChanged: typeof routeChanged;
  initExperiments: typeof initExperiments;
  increaseLaunchCount: typeof increaseLaunchCount;
  initSwWorkout: typeof initSwWorkout;
  checkNotificationsPermission: typeof checkNotificationsPermission;
  checkIfMilestoneCompleted: typeof checkIfMilestoneCompleted;
  setUserAssignments: typeof setUserAssignments;

  @State() authLoaded: SworkitState["auth"]["authLoaded"];
  @State() experiments: SworkitState["experiments"];
  @State() isAuthenticated: SworkitState["auth"]["isAuthenticated"];
  @State() uid: SworkitState["auth"]["user"]["uid"];

  @State() launchCount: SworkitState["localSettings"]["launchCount"];
  @State() preloadError: SworkitState["workoutPlayer"]["preloadError"];
  @State() oAuthInitiated: SworkitState["auth"]["oAuthInitiated"];
  @Watch("preloadError")
  async preloadErrorChanged() {
    if (this.preloadError) {
      showPreloadErrorModal();
    }
  }
  @State() source: SworkitState["subscription"]["source"];
  @State() sworkitAdmin: SworkitState["auth"]["sworkitAdmin"];
  @State() orgPermission: SworkitState["auth"]["orgPermission"];
  @State() orgAdmin: SworkitState["auth"]["orgAdmin"];
  @State() orgOwner: SworkitState["auth"]["orgOwner"];
  @State() curriculumOnly: SworkitState["auth"]["curriculumOnly"];
  @State() userAssignments: SworkitState["userAssignments"];
  @State() totalSessions: SworkitState["user"]["allTimeStats"]["totalSessions"];
  @State() user: SworkitState["user"];
  @State() stepSource: SworkitState["user"]["account"]["stepSource"];

  @Watch("totalSessions")
  shouldWeCelebrate(
    newVal: SworkitState["user"]["allTimeStats"]["totalSessions"],
    oldVal: SworkitState["user"]["allTimeStats"]["totalSessions"],
  ) {
    this.checkIfMilestoneCompleted(oldVal, newVal);
  }

  async componentWillLoad() {
    try {
      Preferences.configure({});
      await Preferences.migrate();
    } catch (err) {
      console.error("storage error", err);
    }
    const storedState = await loadStoredState();
    console.log({ storedState });
    const initialStore = await configureStore(
      storedState as Partial<SworkitState>,
    );
    const iframe = isInIframe();
    await initSwApi(storedState, iframe);
    if (!iframe) {
      await initRemoteConfig();
    }
    console.log("ignoring remote config");
    const oAuthInitiated = await getStorageKey("oauth-login");
    store.setStore(initialStore);
    store.getStore().subscribe(() => {
      const currentState: SworkitState = store.getState();
      saveState(currentState);
    });
    store.mapDispatchToProps(this, {
      getAuth,
      resetInactivityReminder,
      setInitialAudioDucking,
      listenToAppStateChange,
      routeChanged,
      initExperiments,
      increaseLaunchCount,
      initSwWorkout,
      checkNotificationsPermission,
      checkIfMilestoneCompleted,
      setUserAssignments,
    });
    this.unsubscribe = store.mapStateToProps(this, (state: SworkitState) => {
      const {
        auth: {
          authLoaded,
          isAuthenticated,
          sworkitAdmin,
          orgPermission,
          orgAdmin,
          orgOwner,
          curriculumOnly,
        },
        subscription: { source },
        user,
        user: {
          account: { stepSource },
          allTimeStats: { totalSessions },
        },
        workoutPlayer: { preloadError },
        localSettings: { launchCount },
        userAssignments,
        experiments,
      } = state;
      return {
        authLoaded,
        isAuthenticated,
        source,
        sworkitAdmin,
        launchCount,
        experiments,
        totalSessions,
        preloadError,
        oAuthInitiated,
        orgPermission,
        orgAdmin,
        orgOwner,
        curriculumOnly,
        userAssignments,
        user,
        stepSource,
      };
    });
    if (!isInIframe()) {
      this.getAuth();
    }
    this.initSwWorkout();
    this.removeLoading();

    amplitudeInit();

    this.checkNotificationsPermission();
    if (!isInIframe()) {
      this.initExperiments();
    }
  }

  async componentDidLoad() {
    try {
      await SplashScreen.hide();
    } catch (err) {
      console.info("splash error", err);
    }
    try {
      App.addListener("appUrlOpen", (data) =>
        this.openURL(data, this.showLoading),
      );
    } catch (err) {
      console.info(err);
    }

    this.resetInactivityReminder();
    getAvailableNativeHealthApps().then(() => syncAllHealth(this.user));

    initWatchTranslations();

    /*
      Handle service worker updates correctly.
      This code will show a toast letting the
      user of the PWA know that there is a
      new version available. When they click the
      reload button it then reloads the page
      so that the new service worker can take over
      and serve the fresh content
    */
    window.addEventListener("swUpdate", () => this.showToast());
    
    // Initialize dark mode manager
    darkModeManager.initialize();
    
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
      await this.setupSafeAreaInsets();
    }
    
    initGDPR();
    this.setInitialAudioDucking();
    this.listenToAppStateChange();
    this.increaseLaunchCount();
    sendSworkitOpenedToAmplitude();
    amplitudeSetUserProperties({ session_count: this.launchCount });
    if (this.launchCount === 1) {
      amplitudeLogEvent(AmplitudeEvents.opened_sworkit_first_time);
    }
  }

  async disconnectedCallback() {
    this.unsubscribe();
    // Clean up dark mode manager
    darkModeManager.cleanup();
    // Clean up when component unmounts
    await SafeArea.removeAllListeners();
  }

  async showToast() {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration || !registration.waiting) {
      // If there is no registration, this is the first service
      // worker to be installed. registration.waiting is the one
      // waiting to be activiated.
      return;
    }
    showNewVersionAvailableToast();
  }

  //??
  async showLoading() {
    const loading = await loadingController.create({});
    return loading.present();
  }

  async hideLoading() {
    return (document.querySelector("ion-loading") as any).dismiss();
  }

  async openURL(data: URLOpenListenerEvent, showLoading: () => Promise<void>) {
    if (data) {
      await showLoading();
      console.debug("open url", data);
      const url = data.url;
      if (url.includes("sworkit://?workout")) {
        // used for old share pages e.g. http://m.sworkit.com/share?w=Z1pfN1szD4
        const shareID = url.split("=")[1];
        await document.querySelector("ion-router").push(`/share/${shareID}`);
      } else if (url.includes("sworkit://")) {
        const navigateTo = url.split("sworkit://")[1];
        await document.querySelector("ion-router").push(navigateTo);
        await this.hideLoading();
      } else if (url.includes("/import")) {
        const shareID = url.split("=")[1];
        await document.querySelector("ion-router").push(`/share/${shareID}`);
      } else if (url.includes(".sworkit.com")) {
        const navigateTo = url.split(".sworkit.com")[1];
        await document.querySelector("ion-router").push(navigateTo);
        await this.hideLoading();
      } else {
        this.hideLoading();
      }
    }
  }

  private updateSafeAreaInsets(insets: { top: number; bottom: number; left: number; right: number }) {
    // console.log('Updating safe area insets:', insets);
    Object.entries(insets).forEach(([key, value]) => {
      const cssVar = `--ion-safe-area-${key}`;
      document.documentElement.style.setProperty(cssVar, `${value}px`);
      // console.log(`Set ${cssVar} to ${value}px`);
    });
  }

  private async setupSafeAreaInsets() {
    // Initial setup
    const { insets } = await SafeArea.getSafeAreaInsets();
    // console.log('Initial safe area insets:', insets);
    this.updateSafeAreaInsets(insets);

    // Listen for changes
    await SafeArea.addListener("safeAreaChanged", ({ insets }) => {
      // console.log('Safe area changed:', insets);
      this.updateSafeAreaInsets(insets);
    });
  }

  removeLoading() {
    const loading = document.querySelector("sw-loading");
    loading ? loading.remove() : null;
  }

  @Listen("ionRouteDidChange")
  routeChangedHandler(event: CustomEvent<RouterEventDetail>) {
    console.log("routeChangedHandler", event.detail);
    this.routeChanged(event.detail);
  }

  render() {
    return (
      <ion-app>
        {renderRouter(
          this.authLoaded,
          this.isAuthenticated,
          this.source === "before-paid-only",
          this.sworkitAdmin,
          this.orgPermission,
          this.orgAdmin,
          this.orgOwner,
          this.curriculumOnly,
          this.oAuthInitiated,
          this.experiments,
        )}
        <ion-router-outlet
          id="sw-nav"
          animated={true}
          animation={getMode() === "ios" ? iosTransitionAnimation : undefined}
        />
        <sw-audio />
      </ion-app>
    );
  }
}
